import React from 'react';
import { useMutation } from '@tanstack/react-query';
import { Navigate } from 'react-router-dom';
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  FormControl,
  FormLabel,
  Heading,
  Image,
  Input,
  InputGroup,
  InputRightElement,
  Stack,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { Icon } from '@chakra-ui/react';
import { MdOutlineRemoveRedEye } from 'react-icons/md';
import { RiEyeCloseLine } from 'react-icons/ri';
import DefaultAuth from 'layouts/auth/Default';
import authApi from 'services/auth.api';
import useAuth from 'hooks/useAuth';
import Surface from 'components/ui/Surface';

function SignIn() {
  const { login, refetchSession, isAuthenticated } = useAuth();
  const [show, setShow] = React.useState(false);
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const textColor = useColorModeValue('navy.700', 'white');
  const textColorSecondary = useColorModeValue(
    'secondaryGray.700',
    'secondaryGray.600',
  );

  const mutation = useMutation({
    mutationFn: authApi.login,
    onSuccess: async (response) => {
      login(response.data.token);
      await refetchSession();
    },
  });

  if (isAuthenticated) {
    return <Navigate to="/admin/dashboard" replace />;
  }

  return (
    <DefaultAuth>
      <Stack spacing="8">
        <Box>
          <Box
            display="inline-flex"
            alignItems="center"
            justifyContent="center"
            p="12px"
            mb="22px"
            borderRadius="28px"
            bg="linear-gradient(145deg, rgba(255,255,255,0.14), rgba(111, 66, 193, 0.10))"
            border="1px solid"
            borderColor="rgba(162,176,210,0.18)"
            boxShadow="0 18px 40px rgba(7, 12, 24, 0.22)"
            backdropFilter="blur(18px)"
          >
            <Box
              overflow="hidden"
              borderRadius="20px"
              bg="white"
              boxShadow="inset 0 1px 0 rgba(255,255,255,0.75)"
            >
              <Image
                src="/logo.png"
                alt="Master Tech Auto"
                w="170px"
                h="74px"
                objectFit="contain"
                display="block"
              />
            </Box>
          </Box>

          <Heading
            color={textColor}
            fontSize={{ base: '34px', md: '40px' }}
            mb="10px"
          >
            Login
          </Heading>
          <Text color={textColorSecondary} fontSize="md">
            Sign in to manage inspection records, upload workflows, and VIN
            history with a clearer admin workspace.
          </Text>
        </Box>

        <Surface w="100%" variant="elevated" p={{ base: '22px', md: '28px' }}>
          {mutation.isError && (
            <Alert
              status="error"
              borderRadius="18px"
              mb="18px"
              bg="red.50"
              color="red.700"
            >
              <AlertIcon />
              {mutation.error?.response?.data?.error?.message ||
                mutation.error?.message ||
                'Unable to sign in.'}
            </Alert>
          )}
          <form
            onSubmit={(event) => {
              event.preventDefault();
              mutation.mutate({ email, password });
            }}
          >
            <FormControl mb="20px" isRequired>
              <FormLabel fontWeight="700" color={textColor}>
                Email
              </FormLabel>
              <Input
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                type="email"
                placeholder="admin@example.com"
                size="lg"
                variant="auth"
              />
            </FormControl>
            <FormControl mb="20px" isRequired>
              <FormLabel fontWeight="700" color={textColor}>
                Password
              </FormLabel>
              <InputGroup size="lg">
                <Input
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  type={show ? 'text' : 'password'}
                  placeholder="Enter your password"
                  variant="auth"
                />
                <InputRightElement>
                  <Icon
                    color="gray.400"
                    cursor="pointer"
                    as={show ? RiEyeCloseLine : MdOutlineRemoveRedEye}
                    onClick={() => setShow((current) => !current)}
                  />
                </InputRightElement>
              </InputGroup>
            </FormControl>
            <Button
              type="submit"
              fontSize="sm"
              variant="brand"
              fontWeight="600"
              w="100%"
              h="54px"
              borderRadius="18px"
              isLoading={mutation.isPending}
            >
              Sign In
            </Button>
          </form>
        </Surface>
      </Stack>
    </DefaultAuth>
  );
}

export default SignIn;
